import { useEffect, useState } from "react";
import AppShell from "@/components/AppShell";
import api from "@/lib/api";
import { toast } from "sonner";
import { Check, X, ChevronLeft, ChevronRight, Calendar, Sparkles, Trophy } from "lucide-react";
import { Link } from "react-router-dom";
import ProgramHistoryList from "@/components/ProgramHistoryList";

const DAYS = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];
const TYPE_COLORS = {
  easy: "#71c4ff", long: "#d7ff00", threshold: "#ff8800", tempo: "#ff8800",
  vo2: "#ff3b30", hill: "#ff6b00", recovery: "#7fffd4", strength: "#c084fc",
  mobility: "#a3e635", rest: "#444",
};

export default function Training() {
  const [full, setFull] = useState(null);
  const [weekIdx, setWeekIdx] = useState(0);
  const [selected, setSelected] = useState(null);
  const [feel, setFeel] = useState("good");
  const [rpe, setRpe] = useState(6);
  const [notes, setNotes] = useState("");
  const [actualDistance, setActualDistance] = useState("");
  const [actualDuration, setActualDuration] = useState("");
  const [actualPace, setActualPace] = useState("");
  const [intervals, setIntervals] = useState("");
  const [saving, setSaving] = useState(false);

  // When a session is opened, pre-fill the distance/duration inputs with the planned values
  // so athletes who tap "Complete" without editing still get credit for the planned km.
  useEffect(() => {
    if (selected && !selected.completed) {
      setActualDistance(selected.distance_km ? String(selected.distance_km) : "");
      setActualDuration(selected.duration_min ? String(selected.duration_min) : "");
    }
  }, [selected]);

  const load = async () => {
    const { data } = await api.get("/training/full");
    setFull(data);
    setWeekIdx(data.current_week_index || 0);
  };
  useEffect(() => { load(); }, []);

  const complete = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      await api.post(`/training/sessions/${selected.day_index}/complete`, {
        feel, rpe: Number(rpe), notes, week_index: weekIdx,
        actual_distance_km: actualDistance ? Number(actualDistance) : null,
        actual_duration_min: actualDuration ? Number(actualDuration) : null,
        actual_pace: actualPace || null,
        intervals: intervals || null,
      });
      window.dispatchEvent(new CustomEvent("session:logged"));
      toast.success("Session marked complete");
      setSelected(null);
      setNotes(""); setActualDistance(""); setActualDuration(""); setActualPace(""); setIntervals("");
      load();
    } catch { toast.error("Could not save"); }
    setSaving(false);
  };

  if (!full) return <AppShell><div className="text-[#888] font-mono-jp text-xs uppercase tracking-widest">Loading…</div></AppShell>;

  if (!full.program) {
    return (
      <AppShell>
        <div data-testid="training-no-program">
          <div className="font-mono-jp text-[10px] uppercase tracking-widest text-[#d7ff00]">TRAINING PLAN</div>
          <h1 className="font-display text-4xl sm:text-5xl uppercase">Your <span className="text-[#d7ff00]">plan</span></h1>
          <div className="mt-8 bg-[#0a0a0a] border border-[#1f1f1f] p-8 text-center">
            <Sparkles className="w-10 h-10 text-[#d7ff00] mx-auto" />
            <h2 className="mt-4 font-display text-2xl uppercase">Coach JP is preparing<br />your <span className="text-[#d7ff00]">program</span></h2>
            <p className="mt-3 text-[#bbb] text-sm max-w-md mx-auto">It will be ready within 24 hours. Once published, your full 8 to 16 week plan unlocks instantly.</p>
            <Link to="/app/profile" className="mt-5 inline-flex items-center gap-2 border border-[#d7ff00] text-[#d7ff00] font-display tracking-wider uppercase px-5 py-2.5 hover:bg-[#d7ff00] hover:text-black" data-testid="profile-cta">Complete your profile</Link>
          </div>
        </div>
      </AppShell>
    );
  }

  const week = full.weeks[weekIdx];
  const sessions = week?.sessions || [];
  const done = sessions.filter(s => s.completed).length;

  return (
    <AppShell>
      <div data-testid="training-page">
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <div className="font-mono-jp text-[10px] uppercase tracking-widest text-[#d7ff00]">TRAINING · {full.program.name?.toUpperCase()}</div>
            <h1 className="font-display text-3xl sm:text-5xl uppercase">Week {week?.week_number} of {full.total_weeks}</h1>
            <div className="font-mono-jp text-[10px] uppercase tracking-widest text-[#888] mt-1">
              {week?.starts_on} → {week?.ends_on} · {sessions.length} SESSIONS · {week?.total_km}KM
            </div>
          </div>
          <div className="flex items-center gap-px bg-[#1f1f1f]">
            <button onClick={() => setWeekIdx(Math.max(0, weekIdx - 1))} disabled={weekIdx === 0} className="bg-[#0a0a0a] px-3 py-2 text-[#d7ff00] hover:bg-[#1a1a1a] disabled:opacity-30" data-testid="week-prev"><ChevronLeft className="w-4 h-4" /></button>
            <button onClick={() => setWeekIdx(full.current_week_index)} className="bg-[#0a0a0a] px-4 py-2 font-mono-jp text-[10px] uppercase tracking-widest text-[#bbb] hover:text-white" data-testid="week-current">THIS WEEK</button>
            <button onClick={() => setWeekIdx(Math.min(full.total_weeks - 1, weekIdx + 1))} disabled={weekIdx === full.total_weeks - 1} className="bg-[#0a0a0a] px-3 py-2 text-[#d7ff00] hover:bg-[#1a1a1a] disabled:opacity-30" data-testid="week-next"><ChevronRight className="w-4 h-4" /></button>
            <Link to="/app/calendar" className="bg-[#0a0a0a] px-3 py-2 text-[#d7ff00] hover:bg-[#1a1a1a] flex items-center gap-1 font-mono-jp text-[10px] uppercase tracking-widest" data-testid="open-calendar"><Calendar className="w-3.5 h-3.5" /> MONTH</Link>
          </div>
        </div>

        {/* Week progression bar */}
        <div className="mt-6">
          <div className="flex gap-1 overflow-x-auto pb-2 hide-scrollbar">
            {full.weeks.map(w => {
              const isCurrent = w.week_index === full.current_week_index;
              const isActive = w.week_index === weekIdx;
              const completed = w.sessions.filter(s => s.completed).length;
              return (
                <button key={w.week_index} onClick={() => setWeekIdx(w.week_index)}
                  className={`flex-shrink-0 px-3 py-2 border ${isActive ? "border-[#d7ff00] bg-[#d7ff00]/10 text-[#d7ff00]" : "border-[#1f1f1f] text-[#888] hover:text-white"}`}
                  data-testid={`week-tab-${w.week_index}`}>
                  <div className="font-mono-jp text-[10px] uppercase tracking-widest">W{w.week_number}</div>
                  <div className="font-mono-jp text-[9px] text-[#666]">{completed}/{w.sessions.length}</div>
                  {isCurrent && <div className="font-mono-jp text-[8px] text-[#d7ff00] mt-0.5">NOW</div>}
                </button>
              );
            })}
          </div>
        </div>

        {/* Sessions list */}
        <div className="mt-4 space-y-3">
          {sessions.length === 0 && <div className="bg-[#0a0a0a] border border-[#1f1f1f] p-6 text-[#888] text-sm">Rest week — listen to your body.</div>}
          {sessions.map(s => {
            const color = TYPE_COLORS[s.type] || "#d7ff00";
            return (
              <button key={`${s.week_index}-${s.day_index}`} onClick={() => setSelected(s)}
                className={`w-full text-left bg-[#0a0a0a] border ${s.is_today ? "border-[#d7ff00]" : "border-[#1f1f1f]"} p-4 hover:border-[#d7ff00]/40 transition flex items-start gap-3`}
                data-testid={`session-${s.day_index}`}>
                <div className="w-9 h-9 rounded-full bg-[#0e0e0e] flex items-center justify-center font-mono-jp text-[10px] text-white border border-[#222] flex-shrink-0">
                  {DAYS[s.day_index]}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: color }} />
                    <span className="font-display text-base sm:text-lg uppercase truncate">{s.name}</span>
                    {s.is_today && <span className="font-mono-jp text-[9px] uppercase tracking-widest text-[#d7ff00] border border-[#d7ff00] px-1.5">TODAY</span>}
                    {s.completed && <Check className="w-4 h-4 text-[#d7ff00]" />}
                  </div>
                  <div className="font-mono-jp text-[10px] uppercase tracking-widest text-[#666] mt-0.5">{s.date}</div>
                  <div className="mt-2 flex flex-wrap gap-1.5 font-mono-jp text-[10px] uppercase tracking-widest">
                    {s.distance_km && <span className="border border-[#222] px-2 py-0.5 text-[#bbb]">{s.distance_km} KM</span>}
                    {s.pace_target && <span className="border border-[#222] px-2 py-0.5 text-[#bbb]">{s.pace_target}</span>}
                    {s.hr_zone && <span className="border border-[#222] px-2 py-0.5 text-[#d7ff00]">{s.hr_zone}</span>}
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {weekIdx === full.total_weeks - 1 && (
          <div className="mt-6 bg-gradient-to-br from-[#d7ff00]/10 to-transparent border border-[#d7ff00]/40 p-4 flex items-center gap-3" data-testid="final-week-banner">
            <Trophy className="w-5 h-5 text-[#d7ff00]" />
            <div>
              <div className="font-display text-base uppercase tracking-wider">Race week — peak performance</div>
              <div className="font-mono-jp text-[10px] uppercase tracking-widest text-[#bbb]">Trust the taper. The work is done.</div>
            </div>
          </div>
        )}

        {/* Training History — permanent record of every program */}
        <div className="mt-10" data-testid="training-history-section">
          <div className="font-mono-jp text-[10px] uppercase tracking-widest text-[#d7ff00]">TRAINING HISTORY</div>
          <div className="flex items-center justify-between flex-wrap gap-2">
            <h2 className="font-display text-2xl uppercase mt-1">Your <span className="text-[#d7ff00]">programs</span></h2>
            <Link to="/app/progress" className="font-mono-jp text-[10px] uppercase tracking-widest text-[#d7ff00] border border-[#d7ff00]/40 px-3 py-1.5 hover:bg-[#d7ff00]/10" data-testid="view-progress-link">View progress &amp; PBs →</Link>
          </div>
          <div className="mt-4">
            <ProgramHistoryList listEndpoint="/training/history" detailEndpoint={(pid) => `/training/history/${pid}`} testidPrefix="client-history" />
          </div>
        </div>
      </div>

      {selected && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => !saving && setSelected(null)} data-testid="session-modal">
          <div className="bg-[#0a0a0a] border border-[#d7ff00] w-full max-w-lg p-6" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-start mb-3">
              <div>
                <div className="font-mono-jp text-[10px] uppercase tracking-widest text-[#d7ff00]">W{selected.week_number || (weekIdx+1)} · {DAYS[selected.day_index]} · {selected.date}</div>
                <h3 className="font-display text-2xl sm:text-3xl uppercase mt-1">{selected.name}</h3>
              </div>
              {!saving && <button onClick={() => setSelected(null)} className="text-[#888]" data-testid="session-close"><X /></button>}
            </div>
            <p className="text-[#bbb] text-sm">{selected.description}</p>
            {selected.completed ? (
              <div className="mt-5 bg-[#1a1f00] border border-[#d7ff00]/40 p-4">
                <div className="font-mono-jp text-[10px] uppercase tracking-widest text-[#d7ff00] flex items-center gap-1"><Check className="w-3 h-3" /> COMPLETED</div>
                {selected.feedback?.feel && <div className="text-sm text-white mt-2">Feel: <span className="text-[#d7ff00]">{selected.feedback.feel}</span> · RPE {selected.feedback.rpe || "—"}</div>}
                {(selected.feedback?.actual_distance_km || selected.feedback?.actual_duration_min || selected.feedback?.actual_pace) && (
                  <div className="text-sm text-[#ddd] mt-1">
                    {selected.feedback.actual_distance_km && <span className="mr-3">{selected.feedback.actual_distance_km} km</span>}
                    {selected.feedback.actual_duration_min && <span className="mr-3">{selected.feedback.actual_duration_min} min</span>}
                    {selected.feedback.actual_pace && <span>@ {selected.feedback.actual_pace}</span>}
                  </div>
                )}
                {selected.feedback?.intervals && <div className="text-sm text-white mt-2"><span className="font-mono-jp text-[10px] uppercase tracking-widest text-[#bbb]">Intervals:</span><br />{selected.feedback.intervals}</div>}
                {selected.feedback?.notes && <div className="text-sm text-white mt-2"><span className="font-mono-jp text-[10px] uppercase tracking-widest text-[#bbb]">Notes:</span><br />{selected.feedback.notes}</div>}
              </div>
            ) : (
              <>
                <div className="mt-5 font-mono-jp text-[10px] uppercase tracking-widest text-[#bbb]">How was it?</div>
                <div className="mt-2 grid grid-cols-3 sm:grid-cols-6 gap-1">
                  {["great","good","average","tired","very_tired","pain"].map(f => (
                    <button key={f} onClick={() => setFeel(f)} className={`px-2 py-2 font-mono-jp text-[10px] uppercase tracking-widest border ${feel === f ? "border-[#d7ff00] text-[#d7ff00] bg-[#d7ff00]/10" : "border-[#222] text-[#bbb]"}`} data-testid={`feel-${f}`}>{f.replace("_"," ")}</button>
                  ))}
                </div>
                <div className="mt-3 font-mono-jp text-[10px] uppercase tracking-widest text-[#bbb]">RPE · <span className="text-[#d7ff00]">{rpe}/10</span></div>
                <input type="range" min={1} max={10} value={rpe} onChange={e => setRpe(e.target.value)} className="w-full accent-[#d7ff00]" data-testid="rpe-slider" />

                <div className="mt-4 grid grid-cols-3 gap-2">
                  <div>
                    <label className="font-mono-jp text-[9px] uppercase tracking-widest text-[#bbb]">Distance (km)</label>
                    <input type="number" step="0.1" value={actualDistance} onChange={e => setActualDistance(e.target.value)} placeholder={selected.distance_km ? String(selected.distance_km) : "—"}
                      className="mt-1 w-full bg-[#0e0e0e] border border-[#222] focus:border-[#d7ff00] outline-none px-2.5 py-2 text-sm text-white" data-testid="actual-distance" />
                  </div>
                  <div>
                    <label className="font-mono-jp text-[9px] uppercase tracking-widest text-[#bbb]">Duration (min)</label>
                    <input type="number" value={actualDuration} onChange={e => setActualDuration(e.target.value)} placeholder={selected.duration_min ? String(selected.duration_min) : "—"}
                      className="mt-1 w-full bg-[#0e0e0e] border border-[#222] focus:border-[#d7ff00] outline-none px-2.5 py-2 text-sm text-white" data-testid="actual-duration" />
                  </div>
                  <div>
                    <label className="font-mono-jp text-[9px] uppercase tracking-widest text-[#bbb]">Pace</label>
                    <input value={actualPace} onChange={e => setActualPace(e.target.value)} placeholder={selected.pace_target || "5:12"}
                      className="mt-1 w-full bg-[#0e0e0e] border border-[#222] focus:border-[#d7ff00] outline-none px-2.5 py-2 text-sm text-white" data-testid="actual-pace" />
                  </div>
                </div>

                <label className="mt-3 block font-mono-jp text-[10px] uppercase tracking-widest text-[#bbb]">Interval splits / lap times (optional)</label>
                <textarea rows={2} placeholder="e.g. 400m × 8: 1:25, 1:24, 1:26, 1:24, 1:23, 1:25, 1:23, 1:22" value={intervals} onChange={e => setIntervals(e.target.value)} className="mt-1 w-full bg-[#0e0e0e] border border-[#222] focus:border-[#d7ff00] outline-none px-3 py-2 text-sm text-white" data-testid="session-intervals" />

                <label className="mt-3 block font-mono-jp text-[10px] uppercase tracking-widest text-[#bbb]">Note to Coach JP</label>
                <textarea rows={3} placeholder="How did it feel? Anything Coach should know?" value={notes} onChange={e => setNotes(e.target.value)} className="mt-1 w-full bg-[#0e0e0e] border border-[#222] focus:border-[#d7ff00] outline-none px-3 py-2 text-sm text-white" data-testid="session-notes" />

                <button onClick={complete} disabled={saving} className="mt-4 w-full bg-[#d7ff00] text-black font-display uppercase tracking-wider py-3 hover:bg-white disabled:opacity-50" data-testid="mark-complete-btn">
                  {saving ? "Saving…" : "Mark complete"}
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </AppShell>
  );
}
